import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, DollarSign, MessageSquare, Info } from 'lucide-react';

interface ApproveBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: (discountAmount: number, ownerComment: string) => void;
  booking: {
    id: number;
    property_title: string;
    total_price: number;
    total_nights: number;
    check_in: string;
    check_out: string;
  };
  loading?: boolean;
}

const ApproveBookingModal = ({
  isOpen,
  onClose,
  onApprove,
  booking,
  loading = false
}: ApproveBookingModalProps) => {
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [ownerComment, setOwnerComment] = useState<string>('');

  const handleApprove = () => {
    const discountText = discountAmount > 0 ? ` com desconto de R$ ${discountAmount.toLocaleString('pt-BR')}` : '';
    const commentText = ownerComment ? ` e comentário "${ownerComment.substring(0, 50)}${ownerComment.length > 50 ? '...' : ''}"` : '';
    
    const confirmed = window.confirm(`Tem certeza que deseja aprovar esta reserva${discountText}${commentText}?`);
    
    if (confirmed) {
      onApprove(discountAmount, ownerComment);
    }
  };

  const handleClose = () => {
    setDiscountAmount(0);
    setOwnerComment('');
    onClose();
  };

  const finalPrice = booking.total_price - discountAmount;
  const hasDiscount = discountAmount > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Aprovar Reserva
          </DialogTitle>
          <DialogDescription>
            Confirme os detalhes da reserva e adicione um desconto ou comentário se desejar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações da Reserva */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-semibold mb-3">{booking.property_title}</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Check-in:</span>
                <p className="font-medium">{new Date(booking.check_in).toLocaleDateString('pt-BR')}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Check-out:</span>
                <p className="font-medium">{new Date(booking.check_out).toLocaleDateString('pt-BR')}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Noites:</span>
                <p className="font-medium">{booking.total_nights}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Valor Original:</span>
                <p className="font-medium">R$ {booking.total_price.toLocaleString('pt-BR')}</p>
              </div>
            </div>
          </div>

          {/* Campo de Desconto */}
          <div className="space-y-2">
            <Label htmlFor="discount" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Desconto (R$)
            </Label>
            <Input
              id="discount"
              type="number"
              min="0"
              max={booking.total_price}
              step="0.01"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(Number(e.target.value))}
              placeholder="0,00"
              className="text-lg"
            />
            <p className="text-xs text-muted-foreground">
              Digite o valor do desconto em reais (máximo: R$ {booking.total_price.toLocaleString('pt-BR')})
            </p>
          </div>

          {/* Campo de Comentário */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Comentário do Proprietário (Opcional)
            </Label>
            <Textarea
              id="comment"
              value={ownerComment}
              onChange={(e) => setOwnerComment(e.target.value)}
              placeholder="Adicione um comentário para o hóspede..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Este comentário será enviado junto com a confirmação da reserva.
            </p>
          </div>

          {/* Resumo do Valor */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Valor Original:</span>
              <span className="text-sm">R$ {booking.total_price.toLocaleString('pt-BR')}</span>
            </div>
            {hasDiscount && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Desconto:</span>
                <Badge variant="secondary" className="bg-red-100 text-red-800">
                  -R$ {discountAmount.toLocaleString('pt-BR')}
                </Badge>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-primary/20">
              <span className="font-semibold">Valor Final:</span>
              <span className="text-lg font-bold text-primary">
                R$ {finalPrice.toLocaleString('pt-BR')}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleApprove} 
            disabled={loading || finalPrice < 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? 'Aprovando...' : 'Aprovar Reserva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApproveBookingModal;
